import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { useActiveProfile } from "@/domain/hooks";
import { AuthGuard } from "@/app/AuthGuard";
import { LoginPage } from "@/screens/auth/LoginPage";
import { PortfolioHub, SignalsHub, ControlsHub, InsightsHub } from "@/screens/stratus/HubLayout";
import { FoundationShowcase } from "@/screens/FoundationShowcase";
import { PlaceholderScreen } from "@/screens/PlaceholderScreen";
import { StratusDashboardLayout } from "@/screens/stratus/StratusDashboardLayout";
import { StratusDashboardHome } from "@/screens/stratus/StratusDashboardHome";
import { StratusProjectsPage } from "@/screens/stratus/StratusProjectsPage";
import { AssuranceView } from "@/screens/stratus/AssuranceView";
import { ExceptionsView } from "@/screens/stratus/ExceptionsView";
import { ComparisonView } from "@/screens/stratus/ComparisonView";
import { AccessView } from "@/screens/stratus/AccessView";
import { ComplianceView } from "@/screens/stratus/ComplianceView";
// TODO(heimdall): AI analyst not built yet — re-enable when Heimdall ships.
// import { HeimdallQueue } from "@/screens/stratus/HeimdallQueue";
import { FunderDashboardHome } from "@/screens/funder/FunderDashboardHome";
import { FunderProjectsPage } from "@/screens/funder/FunderProjectsPage";
import { ReportsGallery, ReportView } from "@/screens/reports/ReportsSurface";
import { ContractorsTab } from "@/screens/stratus/ContractorsTab";
import { ApprovalQueueTab } from "@/screens/stratus/ApprovalQueueTab";
import { LeaksTab, AnalyticsTab, AuditTrailTab } from "@/screens/stratus/StratusTabs";
import { KycReviewQueue } from "@/screens/stratus/KycReviewQueue";
import { FunderDashboardLayout } from "@/screens/funder/FunderDashboardLayout";
import {
  FunderApprovalsTab,
  FunderAnalyticsTab,
  FunderAuditTab,
} from "@/screens/funder/FunderTabs";
import { ProgrammeDashboard } from "@/screens/programme/ProgrammeDashboard";
import { ProjectManagementView } from "@/screens/project/ProjectManagementView";
import { ProjectSummaryPage } from "@/screens/project/ProjectSummaryPage";
import { SubProjectView } from "@/screens/project/SubProjectView";
import {
  WorkspaceProjectsPage,
  WorkspaceApprovalsPage,
  WorkspaceAnalyticsPage,
} from "@/screens/programme/WorkspacePages";
import { FinanceControlsPage, WorkspaceSettingsPage } from "@/screens/programme/WorkspaceHubs";
import { PlanningSurface } from "@/screens/planning/PlanningSurface";
import { UploadWizard } from "@/screens/ai-upload/UploadWizard";
import { OnboardingWizard } from "@/screens/onboarding/OnboardingWizard";
import { InviteContractorPage } from "@/screens/onboarding/InviteContractorPage";
import { WorkspaceRaiseAsk, WorkspaceSubmitEvidence, WorkspaceEvidencePicker } from "@/screens/project/ContractorCapture";
import { ReporterShell } from "@/screens/reporter/ReporterShell";
import { ReporterInbox } from "@/screens/reporter/Inbox";
import { RaiseAsk } from "@/screens/reporter/RaiseAsk";
import { SubmitEvidence } from "@/screens/reporter/SubmitEvidence";
import { ReporterEvidenceIndex, ReporterProfile } from "@/screens/reporter/ReporterMisc";
import { PublicProjectPage } from "@/screens/public/PublicProjectPage";
import { SiteVisitsList } from "@/screens/sitevisits/SiteVisitsList";
import { SiteVisitView } from "@/screens/sitevisits/SiteVisitView";
import { SiteVisitQuestionsSettings } from "@/screens/sitevisits/SiteVisitQuestionsSettings";

const MapSurface = lazy(() =>
  import("@/screens/map/MapSurface").then((m) => ({ default: m.MapSurface })),
);

function MapRoute() {
  return (
    <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-mute">Loading map…</div>}>
      <MapSurface />
    </Suspense>
  );
}

function RootRedirect() {
  const profile = useActiveProfile();
  return <Navigate to={profile.landing} replace />;
}

/** Param-preserving redirect from the old flat site-visit detail path into Portfolio. */
function VisitRedirect() {
  const { visitId } = useParams();
  return <Navigate to={`/stratus/portfolio/site-visits/${visitId}`} replace />;
}

/** Param-preserving redirect for moved workspace routes. */
function WsRedirect({ to }: { to: (workspaceId: string) => string }) {
  const { workspaceId } = useParams();
  return <Navigate to={to(workspaceId ?? "")} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes — no auth required */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/foundation" element={<FoundationShowcase />} />
      <Route path="/onboard/:inviteToken" element={<OnboardingWizard />} />

      {/* Protected routes — AuthGuard checks session and redirects to /login if needed */}
      <Route element={<AuthGuard />}>
      <Route path="/" element={<RootRedirect />} />

      {/* Stakeholder view(used to be Public Page) — session-gated, then role-gated inside the page to
          funder / contractor / oversight (+ ops). Workspace-scoped form resolves
          API-backed projects (non-demo mode); the bare form is kept for legacy
          seed links and falls back to a seed search. */}
      <Route path="/public/:workspaceId/:projectId" element={<PublicProjectPage />} />
      <Route path="/public/:projectId" element={<PublicProjectPage />} />

      {/* Stratus — IA redesign: 7 hub destinations + System; detail/analysis are tabs or drill-ins. */}
      <Route path="/stratus" element={<StratusDashboardLayout />}>
        <Route index element={<StratusDashboardHome />} />

        {/* Portfolio hub — the deal-grid home replaced the over-aggregated programme/project
            lists; only the cross-deal Map and Site visits survive as portfolio-scoped surfaces
            (reached from the home). The list tabs redirect back to the grid. */}
        <Route path="portfolio" element={<PortfolioHub />}>
          <Route index element={<Navigate to="/stratus" replace />} />
          <Route path="programmes" element={<Navigate to="/stratus" replace />} />
          <Route path="projects" element={<Navigate to="/stratus/projects" replace />} />
          <Route path="map" element={<div className="h-[70vh] min-h-[420px]"><MapRoute /></div>} />
          <Route path="site-visits" element={<SiteVisitsList />} />
        </Route>
        <Route path="portfolio/site-visits/settings" element={<SiteVisitQuestionsSettings />} />
        <Route path="portfolio/site-visits/:visitId" element={<SiteVisitView />} />

        {/* Signals hub — the merged oversight inbox */}
        <Route path="signals" element={<SignalsHub />}>
          {/* TODO(heimdall): AI analyst not built yet — re-enable the heimdall route and
              restore the index redirect to it when Heimdall ships. */}
          <Route index element={<Navigate to="/stratus/signals/exceptions" replace />} />
          {/* <Route path="heimdall" element={<HeimdallQueue />} /> */}
          <Route path="exceptions" element={<ExceptionsView />} />
          <Route path="leaks" element={<LeaksTab />} />
        </Route>

        <Route path="approvals" element={<ApprovalQueueTab />} />

        {/* Controls hub — verification & accountability */}
        <Route path="controls" element={<ControlsHub />}>
          <Route index element={<Navigate to="/stratus/controls/assurance" replace />} />
          <Route path="assurance" element={<AssuranceView />} />
          <Route path="compliance" element={<ComplianceView />} />
          <Route path="contractors" element={<ContractorsTab />} />
          <Route path="access" element={<AccessView />} />
        </Route>

        {/* Insights hub — pace + comparison */}
        <Route path="insights" element={<InsightsHub />}>
          <Route index element={<Navigate to="/stratus/insights/pace" replace />} />
          <Route path="pace" element={<AnalyticsTab />} />
          <Route path="comparison" element={<ComparisonView />} />
        </Route>

        <Route path="reports" element={<ReportsGallery />} />
        <Route path="reports/:reportId" element={<ReportView />} />
        <Route path="audit" element={<AuditTrailTab />} />

        {/* Redirects from the old flat routes — keep deep-links and muscle memory alive.
            The programme list is gone (the deal grid replaced it) → home. Projects is
            real again — a genuine cross-deal list, reachable from the nav too. */}
        <Route path="programmes" element={<Navigate to="/stratus" replace />} />
        <Route path="projects" element={<StratusProjectsPage />} />
        <Route path="map" element={<Navigate to="/stratus/portfolio/map" replace />} />
        <Route path="site-visits" element={<Navigate to="/stratus/portfolio/site-visits" replace />} />
        <Route path="site-visits/:visitId" element={<VisitRedirect />} />
        {/* TODO(heimdall): point back to /stratus/signals/heimdall when the AI analyst ships. */}
        <Route path="heimdall" element={<Navigate to="/stratus/signals/exceptions" replace />} />
        <Route path="exceptions" element={<Navigate to="/stratus/signals/exceptions" replace />} />
        <Route path="leaks" element={<Navigate to="/stratus/signals/leaks" replace />} />
        <Route path="assurance" element={<Navigate to="/stratus/controls/assurance" replace />} />
        <Route path="compliance" element={<Navigate to="/stratus/controls/compliance" replace />} />
        <Route path="contractors" element={<Navigate to="/stratus/controls/contractors" replace />} />
        <Route path="access" element={<Navigate to="/stratus/controls/access" replace />} />
        <Route path="analytics" element={<Navigate to="/stratus/insights/pace" replace />} />
        <Route path="comparison" element={<Navigate to="/stratus/insights/comparison" replace />} />
      </Route>
      {/* P0.3: one canonical KYC review queue route; old paths redirect here */}
      <Route path="/stratus/controls/kyc" element={<KycReviewQueue />} />
      <Route path="/stratus/controls/kyc/:id" element={<KycReviewQueue />} />
      <Route path="/stratus/contractors/kyc" element={<Navigate to="/stratus/controls/kyc" replace />} />
      <Route path="/stratus/contractors/:id/kyc" element={<Navigate to="/stratus/controls/kyc" replace />} />

      {/* Funder */}
      <Route path="/funder" element={<FunderDashboardLayout />}>
        <Route index element={<FunderDashboardHome />} />
        {/* The funder Programmes tab is redundant with the deal grid → home. */}
        <Route path="programmes" element={<Navigate to="/funder" replace />} />
        <Route path="projects" element={<FunderProjectsPage />} />
        <Route path="approvals" element={<FunderApprovalsTab />} />
        <Route path="insights" element={<FunderAnalyticsTab />} />
        <Route path="analytics" element={<Navigate to="/funder/insights" replace />} />
        <Route path="reports" element={<ReportsGallery />} />
        <Route path="reports/:reportId" element={<ReportView />} />
        <Route path="audit" element={<FunderAuditTab />} />
      </Route>

      {/* Per-deal workspace */}
      <Route path="/workspace/:workspaceId" element={<ProgrammeDashboard />} />
      <Route path="/workspace/:workspaceId/projects" element={<WorkspaceProjectsPage />} />
      <Route path="/workspace/:workspaceId/projects/:projectId" element={<ProjectManagementView />} />
      <Route path="/workspace/:workspaceId/projects/:projectId/summary" element={<ProjectSummaryPage />} />
      <Route path="/workspace/:workspaceId/projects/:projectId/sub/:subProjectId" element={<SubProjectView />} />
      <Route path="/workspace/:workspaceId/projects/:projectId/sub/:subProjectId/summary" element={<ProjectSummaryPage />} />
      <Route path="/workspace/:workspaceId/plan" element={<PlanningSurface />} />
      {/* Contractor-lead capture flows (P0.1): raise, submit evidence */}
      <Route path="/workspace/:workspaceId/raise" element={<WorkspaceRaiseAsk />} />
      <Route path="/workspace/:workspaceId/submit-evidence" element={<WorkspaceEvidencePicker />} />
      <Route path="/workspace/:workspaceId/submit-evidence/:askId" element={<WorkspaceSubmitEvidence />} />
      <Route path="/workspace/:workspaceId/approvals" element={<WorkspaceApprovalsPage />} />
      <Route path="/workspace/:workspaceId/analytics" element={<WorkspaceAnalyticsPage />} />
      {/* Evidence is reviewed inside Approvals now (proof modal per ask) — keep the deep-link alive. */}
      <Route path="/workspace/:workspaceId/evidence" element={<WsRedirect to={(id) => `/workspace/${id}/approvals`} />} />
      {/* Finance & controls hub (spending authority + connections) */}
      <Route path="/workspace/:workspaceId/finance" element={<FinanceControlsPage />} />
      <Route path="/workspace/:workspaceId/finance/connections" element={<FinanceControlsPage />} />
      {/* Settings hub (participants + audit) */}
      <Route path="/workspace/:workspaceId/settings" element={<WorkspaceSettingsPage />} />
      <Route path="/workspace/:workspaceId/settings/audit" element={<WorkspaceSettingsPage />} />
      <Route path="/workspace/:workspaceId/onboarding" element={<UploadWizard />} />
      <Route path="/workspace/:workspaceId/onboard-contractor" element={<InviteContractorPage />} />
      {/* Redirects from the old flat workspace routes */}
      <Route path="/workspace/:workspaceId/authority" element={<WsRedirect to={(id) => `/workspace/${id}/finance`} />} />
      <Route path="/workspace/:workspaceId/connections" element={<WsRedirect to={(id) => `/workspace/${id}/finance/connections`} />} />
      <Route path="/workspace/:workspaceId/milestones" element={<WsRedirect to={(id) => `/workspace/${id}/projects`} />} />
      {/* Project health moved into the Projects surface; the old health tab deep-link lands there. */}
      <Route path="/workspace/:workspaceId/plan/health" element={<WsRedirect to={(id) => `/workspace/${id}/projects`} />} />
      <Route path="/workspace/:workspaceId/team" element={<WsRedirect to={(id) => `/workspace/${id}/settings`} />} />
      <Route path="/workspace/:workspaceId/audit" element={<WsRedirect to={(id) => `/workspace/${id}/settings/audit`} />} />

      {/* Reporter PWA */}
      <Route path="/reporter" element={<ReporterShell />}>
        <Route index element={<ReporterInbox />} />
        <Route path="raise" element={<RaiseAsk />} />
        <Route path="evidence" element={<ReporterEvidenceIndex />} />
        <Route path="evidence/:askId" element={<SubmitEvidence />} />
        <Route path="profile" element={<ReporterProfile />} />
      </Route>
      </Route>{/* end AuthGuard */}

      <Route path="*" element={<PlaceholderScreen />} />
    </Routes>
  );
}
